import { inject, Inject, Injectable, signal } from '@angular/core';
import {
  AuthFlowType,
  B2BPasswordStrengthCheckResponse,
  B2BProducts,
  createStytchB2BClient,
  StytchB2BClient,
} from '@stytch/vanilla-js/b2b';
import { getSafeUrl, LocationService } from '@callrail/looky/util';
import {
  StytchBaseUrl,
  StytchPublicToken,
  StytchWhiteLabel,
} from '../anubis-init/stytch-public-token';
import { ParamMap } from '@angular/router';
import { ErrorReporterService } from '../error-reporter/error-reporter.service';
import { RedirectService } from '../redirect/redirect.service';
import { from } from 'rxjs';

const INTERMEDIATE_EXCHANGE = 'B2B_DISCOVERY_INTERMEDIATE_SESSION_EXCHANGE';
const AUTH_COMPLETE = 'AUTHENTICATE_FLOW_COMPLETE';
const CHARACTER_TYPES = {
  has_lower_case: 'a lowercase letter',
  has_upper_case: 'an uppercase letter',
  has_digit: 'a number',
  has_symbol: 'a symbol',
};

export function passwordFeedback({
  breached_password,
  luds_feedback,
}: B2BPasswordStrengthCheckResponse): string {
  if (breached_password) {
    return 'This password has appeared in a data breach. Please choose a different one.';
  }
  const { missing_characters, missing_complexity } = luds_feedback;
  const missingTypes = Object.entries(CHARACTER_TYPES)
    .filter(([key]) => !luds_feedback[key])
    .map(([, label]) => label);
  return [
    missing_characters > 0 &&
      `Add ${missing_characters} more character${missing_characters === 1 ? '' : 's'}.`,
    missing_complexity > 0 &&
      `Add ${missing_complexity} more of: ${missingTypes.join(', ')}.`,
  ]
    .filter(Boolean)
    .join(' ');
}

export interface StytchMfa {
  organization_id: string;
  member_id: string;
  method: 'sms' | 'totp';
}

interface ClientSetup {
  client: StytchB2BClient;
  config: any;
  presentation: any;
  callbacks: any;
}

@Injectable({
  providedIn: 'root',
})
export class StytchService {
  private readonly client: StytchB2BClient;
  private readonly presentation = {
    theme: {
      'font-family': 'Source Sans Pro, sans-serif',
      'container-radius': '0',
      background: 'var(--color-background-primary)',
      border: 'var(--color-background-primary)',
      primary: 'var(--color-attribluetion)',
      'button-radius': '4px',
      'rounded-base': '2px',
    },
    options: {
      hideHeaderText: true,
    },
  };
  private readonly locationService = inject(LocationService);
  private readonly errorReporter = inject(ErrorReporterService);
  private readonly redirectService = inject(RedirectService);

  private orgId = signal<string>(null);
  private whiteLabel = signal(false);

  constructor(
    @Inject(StytchPublicToken) publicToken: string,
    @Inject(StytchBaseUrl) baseUrl: string,
    @Inject(StytchWhiteLabel) whiteLabel: boolean
  ) {
    if (baseUrl) {
      this.client = createStytchB2BClient(publicToken, {
        customBaseUrl: baseUrl,
      });
    } else {
      this.whiteLabel.set(whiteLabel);
      this.client = createStytchB2BClient(publicToken);
    }
  }

  public config(params: ParamMap): ClientSetup {
    const returnToPath =
      params.get('return_to') != null
        ? `?return_to=${new URL(getSafeUrl(params.get('return_to'))).pathname}`
        : '';
    const loginRedirectUrl = `${this.locationService.origin}/authenticate/login${returnToPath}`;
    return {
      client: this.client,
      config: {
        products: this.products,
        authFlowType: AuthFlowType.Discovery,
        directLoginForSingleMembership: true,
        disableCreateOrganization: true,
        ...this.loginOptions(loginRedirectUrl),
        passwordOptions: {
          resetPasswordRedirectURL: loginRedirectUrl,
          discoveryRedirectURL: loginRedirectUrl,
        },
      },
      presentation: this.presentation,
      callbacks: this.callbacks(params),
    };
  }

  public signIn(
    email_address: string,
    password: string,
    organization_id: string
  ) {
    return from(
      this.client.passwords.authenticate({
        email_address,
        password,
        organization_id,
        session_duration_minutes: 60,
      })
    );
  }

  public sendSmsCode({ organization_id, member_id }: StytchMfa) {
    return from(this.client.otps.sms.send({ organization_id, member_id }));
  }

  public confirmMfa(
    { organization_id, member_id, method }: StytchMfa,
    code: string
  ) {
    const options = {
      organization_id,
      member_id,
      code,
      session_duration_minutes: 60,
    };
    return from(
      method === 'totp'
        ? this.client.totp.authenticate(options)
        : this.client.otps.sms.authenticate(options)
    );
  }

  public resetPassword(email_address: string, initialOrg: string) {
    return from(
      this.client.passwords.resetByEmailStart({
        email_address,
        reset_password_redirect_url: `${this.locationService.origin}/authenticate/set-password`,
        login_redirect_url: `${this.locationService.origin}/authenticate/login`,
        organization_id: initialOrg,
        reset_password_expiration_minutes: 10,
      })
    );
  }

  public passwordStrengthCheck(
    passwordToCheck: string,
    email_address?: string
  ) {
    return this.client.passwords.strengthCheck({
      email_address,
      password: passwordToCheck,
    });
  }

  public updatePassword(newPassword: string, resetToken: string) {
    return from(
      this.client.passwords.resetByEmail({
        password: newPassword,
        password_reset_token: resetToken,
        session_duration_minutes: 60,
      })
    );
  }

  private callbacks(params: ParamMap) {
    return {
      onEvent: (event) => {
        if (event.type === INTERMEDIATE_EXCHANGE) {
          this.orgId.set(event.data?.organization?.organization_slug || null);
        }
        if (event.type === AUTH_COMPLETE) {
          if (params.get('return_to')) {
            this.redirectService.redirect(params).subscribe({
              next: ({ redirect_to }) => {
                this.locationService.go(redirect_to, true);
              },
              error: () => {
                this.locationService.go('/', true);
              },
            });
            return;
          }

          if (this.orgId() != null) {
            this.locationService.go(`/analytics/a/${this.orgId()}/home`, true);
          } else {
            this.locationService.go('/', true);
          }
        }
      },
      onError: (error) => {
        this.errorReporter.report(error);
      },
    };
  }

  private get products() {
    // white label
    if (this.whiteLabel()) {
      return [B2BProducts.passwords];
    }

    // CR branded
    return [
      B2BProducts.emailMagicLinks,
      B2BProducts.oauth,
      // B2BProducts.sso,
      B2BProducts.passwords,
    ];
  }

  private loginOptions(loginRedirectUrl: string) {
    const passwordOptions = {
      loginRedirectUrl,
      resetPasswordRedirectURL: loginRedirectUrl,
    };
    const loginAndDiscovery = {
      loginRedirectUrl,
      discoveryRedirectUrl: loginRedirectUrl,
    };

    // white label
    if (this.whiteLabel()) {
      return {
        sessionOptions: {
          sessionDurationMinutes: 60,
        },
        passwordOptions,
      };
    }

    // CR branded
    return {
      sessionOptions: {
        sessionDurationMinutes: 60,
      },
      passwordOptions,
      emailMagicLinksOptions: {
        ...loginAndDiscovery,
      },
      oauthOptions: {
        ...loginAndDiscovery,
        providers: [{ type: 'google', one_tap: false }],
      },
      ssoOptions: {
        loginRedirectURL: loginRedirectUrl,
      },
    };
  }
}
